"use client";

import { useEffect } from "react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import {
  boundaryMonitoringContext,
  captureException,
} from "@/lib/error-monitoring.mjs";

/**
 * Global error boundary for the app shell. Surfaces unexpected client-side
 * errors to users with a retry CTA and reports them to optional browser
 * monitoring when configured.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const router = useRouter();

  useEffect(() => {
    console.error("[lenscoach] unhandled error", error);
    void captureException(
      error,
      boundaryMonitoringContext("app-error-boundary", error),
    );
  }, [error]);

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center px-6 py-20 text-center">
      <h1 className="text-2xl font-semibold font-[family-name:var(--font-syne)]">
        Something broke
      </h1>
      <p className="mt-2 max-w-md text-sm text-muted-foreground">
        We&apos;ve logged the error. Retry in a moment — if it keeps happening,
        reach out to support with this reference:
      </p>
      <code className="mt-2 rounded bg-muted px-2 py-1 text-xs">
        {error.digest ?? "no-digest"}
      </code>
      <div className="mt-6 flex gap-2">
        <Button onClick={reset}>Try again</Button>
        <Button
          variant="outline"
          onClick={() => {
            router.push("/dashboard");
          }}
        >
          Back to dashboard
        </Button>
      </div>
    </div>
  );
}
